/**
 * Predictive Search Module
 * Uses Section Rendering API for accurate product data (prices, variants, etc.)
 */
class PredictiveSearch {
	constructor() {
		// Selectors
		this.selectors = {
			searchForm: '.predictive-search-form-el',
			searchInput: '.predictive-search-input-el',
			searchToggle: '.header-search-toggle',
			searchOverlay: '.predictive-search-overlay',
			searchClose: '.predictive-search-close',
			resultsContainer: '.predictive-search-results',
			container: '.predictive-search-container'
		};

		this.searchForm = document.querySelector(this.selectors.searchForm);
		this.searchInput = document.querySelector(this.selectors.searchInput);
		this.searchToggles = document.querySelectorAll(this.selectors.searchToggle);
		this.searchOverlay = document.querySelector(this.selectors.searchOverlay);
		this.searchClose = document.querySelector(this.selectors.searchClose);
		this.resultsContainer = document.querySelector(this.selectors.resultsContainer);
		this.container = document.querySelector(this.selectors.container);
		this.footer = null;

		this.debounceTimer = null;
		this.debounceDelay = 300;
		this.minSearchLength = 2;
		this.isOpen = false;
		this.currentFocus = -1;
		this.abortController = null;

		this.init();
	}

	init() {
		if (!this.searchForm || !this.searchInput || !this.searchOverlay) return;

		this.attachEventListeners();
		this.calculateViewportHeight();
	}

	attachEventListeners() {
		// Toggle search overlay - attach to ALL toggles
		if (this.searchToggles.length > 0) {
			this.searchToggles.forEach(toggle => {
				toggle.addEventListener('click', (e) => {
					e.preventDefault();
					e.stopPropagation(); // Stop bubbling to prevent startup.js handler
					e.stopImmediatePropagation(); // Try to prevent other listeners on same element
					this.openSearch();
				});
			});
		}

		// Close search overlay
		if (this.searchClose) {
			this.searchClose.addEventListener('click', (e) => {
				e.preventDefault();
				this.closeSearch();
			});
		}

		// Close on overlay click
		if (this.searchOverlay) {
			this.searchOverlay.addEventListener('click', (e) => {
				if (e.target === this.searchOverlay) {
					this.closeSearch();
				}
			});
		}

		// Input events
		this.searchInput.addEventListener('input', (e) => this.handleInput(e));
		this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
		this.searchInput.addEventListener('focus', () => {
			if (window.innerWidth < 768 && this.searchInput.value.length >= this.minSearchLength) {
				this.performSearch(this.searchInput.value);
			}
		});

		// Prevent form submission, handle with custom logic
		this.searchForm.addEventListener('submit', (e) => {
			e.preventDefault();
			this.handleSubmit();
		});

		// Close on escape key
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.isOpen) {
				this.closeSearch();
			}
		});

		// Handle resize for mobile height adjustment
		window.addEventListener('resize', () => {
			this.calculateViewportHeight();
		});
	}

	calculateViewportHeight() {
		// Standard fix for mobile 100vh issue
		const vh = window.innerHeight * 0.01;
		document.documentElement.style.setProperty('--vh', `${vh}px`);
	}

	openSearch() {
		if (this.searchOverlay) {
			this.calculateViewportHeight();
			this.searchOverlay.classList.remove('active'); // Reset first to ensure animation plays if re-opening quickly? No, just add.
			// Small timeout to allow display:flex to apply if it was none
			this.searchOverlay.style.display = 'flex';

			// Force reflow
			void this.searchOverlay.offsetWidth;

			this.searchOverlay.classList.add('active');
			this.isOpen = true;

			// Focus input after animation
			setTimeout(() => {
				this.searchInput.focus();
			}, 100);

			// Prevent body scroll
			document.body.style.overflow = 'hidden';
		}
	}

	closeSearch() {
		if (this.searchOverlay) {
			this.searchOverlay.classList.remove('active');
			this.isOpen = false;
			this.searchInput.value = '';
			this.clearResults();

			// Hide after animation (approx match CSS transition)
			setTimeout(() => {
				if (!this.isOpen) {
					this.searchOverlay.style.display = '';
				}
			}, 300);

			// Restore body scroll
			document.body.style.overflow = '';
		}
	}

	handleInput(e) {
		const query = e.target.value.trim();

		clearTimeout(this.debounceTimer);

		if (query.length < this.minSearchLength) {
			this.clearResults();
			return;
		}

		this.debounceTimer = setTimeout(() => {
			this.performSearch(query);
		}, this.debounceDelay);
	}

	handleKeydown(e) {
		const items = this.resultsContainer?.querySelectorAll('.predictive-search-item');

		if (!items || items.length === 0) return;

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				this.currentFocus++;
				if (this.currentFocus >= items.length) this.currentFocus = 0;
				this.setActiveFocus(items);
				break;

			case 'ArrowUp':
				e.preventDefault();
				this.currentFocus--;
				if (this.currentFocus < 0) this.currentFocus = items.length - 1;
				this.setActiveFocus(items);
				break;

			case 'Enter':
				e.preventDefault();
				if (this.currentFocus > -1 && items[this.currentFocus]) {
					const link = items[this.currentFocus].querySelector('a');
					if (link) link.click();
				} else {
					this.handleSubmit();
				}
				break;
		}
	}

	setActiveFocus(items) {
		items.forEach((item, index) => {
			if (index === this.currentFocus) {
				item.classList.add('active-focus');
				item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			} else {
				item.classList.remove('active-focus');
			}
		});
	}

	handleSubmit() {
		const query = this.searchInput.value.trim();
		if (query.length > 0) {
			window.location.href = `/search?q=${encodeURIComponent(query)}`;
		}
	}

	async performSearch(query) {
		this.showLoading();

		// Cancel previous request
		if (this.abortController) {
			this.abortController.abort();
		}
		this.abortController = new AbortController();

		try {
			// Use Section Rendering API to get properly formatted products with correct prices
			// Note: ensure 'predictive-search' section ID matches the filename or expected section
			const url = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=8&section_id=predictive-search`;

			const response = await fetch(url, {
				signal: this.abortController.signal
			});

			if (!response.ok) {
				throw new Error('Search request failed');
			}

			const html = await response.text();
			this.displayResults(html, query);

		} catch (error) {
			if (error.name === 'AbortError') {
				// Request was aborted, do nothing
				return;
			}
			console.error('Predictive search error:', error);
			this.showError();
		}
	}

	displayResults(html, query) {
		// Create a temporary container to parse the response
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = html;

		// Extract the results content
		const resultsContent = tempDiv.querySelector('.predictive-search-content');

		if (!resultsContent) {
			this.showEmpty(query);
			return;
		}

		// Check if there are any results
		const hasResults = resultsContent.querySelector('.predictive-search-section');

		if (!hasResults) {
			this.showEmpty(query);
			return;
		}

		// Remove existing footer from resultsContent if present (just in case)
		const existingFooterInResults = resultsContent.querySelector('.predictive-search-footer');
		if (existingFooterInResults) {
			existingFooterInResults.remove();
		}

		this.resultsContainer.innerHTML = '';
		this.resultsContainer.appendChild(resultsContent);
		this.currentFocus = -1;

		// Update or create sticky footer
		this.updateFooter(query);
	}

	updateFooter(query) {
		if (!this.container) return;

		// Check if footer already exists in the main container
		if (!this.footer) {
			this.footer = document.createElement('div');
			this.footer.className = 'predictive-search-footer';
			this.container.appendChild(this.footer);
		}

		this.footer.innerHTML = `
      <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search-view-all">
        ${this.getTranslation('viewAll')}
      </a>
    `;
		this.footer.style.display = 'block';
	}

	getTranslation(key) {
		// Safety check for translations
		if (window.theme && window.theme.strings && window.theme.strings.predictiveSearch) {
			return window.theme.strings.predictiveSearch[key];
		}

		// Fallback English translations
		const fallbacks = {
			loading: 'Searching...',
			noResults: 'No results found for "{{ query }}"',
			tryDifferent: 'Try a different search term',
			error: 'An error occurred while searching. Please try again.',
			products: 'Products',
			collections: 'Collections',
			pages: 'Pages',
			articles: 'Articles',
			viewAll: 'View all results',
			soldOut: 'Sold out'
		};

		return fallbacks[key] || '';
	}

	showLoading() {
		this.hideFooter();
		this.resultsContainer.innerHTML = `
      <div class="predictive-search-loading">
        <div class="loading-spinner"></div>
        <p>${this.getTranslation('loading')}</p>
      </div>
    `;
	}

	showEmpty(query) {
		this.hideFooter();
		this.resultsContainer.innerHTML = `
      <div class="predictive-search-empty">
        <p>${this.getTranslation('noResults').replace('{{ query }}', `<strong>${this.escapeHtml(query)}</strong>`)}</p>
        <p class="empty-suggestion">${this.getTranslation('tryDifferent')}</p>
      </div>
    `;
	}

	showError() {
		this.hideFooter();
		this.resultsContainer.innerHTML = `
      <div class="predictive-search-error">
        <p>${this.getTranslation('error')}</p>
      </div>
    `;
	}

	clearResults() {
		if (this.resultsContainer) {
			this.resultsContainer.innerHTML = '';
		}
		this.hideFooter();
		this.currentFocus = -1;
	}

	hideFooter() {
		if (this.footer) {
			this.footer.style.display = 'none';
		}
	}

	escapeHtml(text) {
		const div = document.createElement('div');
		div.textContent = text;
		return div.innerHTML;
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		new PredictiveSearch();
	});
} else {
	new PredictiveSearch();
}
